import { Sources } from "@modules/purchase/types/Source";
import { TranslationKeys } from "src/@types/i18next";

export type ConsumerFinancingStep = {
  id: number;
  nameTranslation: TranslationKeys["common"] | TranslationKeys["purchase"];
  alternateAdminNameTranslation?:
    | TranslationKeys["common"]
    | TranslationKeys["admin"]
    | TranslationKeys["purchase"];
  order: number;
  href: string;
  isPointOfNoReturn: boolean;
  introHref?: string;
};

export const consumerFinancingIntroStep: ConsumerFinancingStep = {
  id: 0,
  nameTranslation: "common:introduction",
  order: 0,
  href: "/purchase/consumer/introduction",
  isPointOfNoReturn: false,
};

export const consumerFinancingDetailsStep: ConsumerFinancingStep = {
  id: 13,
  nameTranslation: "common:vehicle_details",
  order: 1,
  href: "/purchase/consumer/vehicle-details",
  isPointOfNoReturn: false,
  introHref: "/purchase/consumer/introduction",
};

export const consumerFinancingPersonalInfoStep: ConsumerFinancingStep = {
  id: 14,
  nameTranslation: "purchase:personal_info",
  order: 2,
  href: "/purchase/consumer/personal-info",
  isPointOfNoReturn: false,
};

export const consumerFinancingFinancingStep: ConsumerFinancingStep = {
  id: 15,
  nameTranslation: "common:financing",
  order: 4,
  href: "/purchase/consumer/financing",
  isPointOfNoReturn: false,
};

export const consumerFinancingCoveragesStep: ConsumerFinancingStep = {
  id: 16,
  nameTranslation: "purchase:coverages",
  order: 3,
  href: "/purchase/consumer/coverages",
  isPointOfNoReturn: false,
  introHref: "/purchase/consumer/coverages-intro",
};

export const consumerFinancingReviewStep: ConsumerFinancingStep = {
  id: 17,
  nameTranslation: "purchase:review",
  order: 5,
  href: "/purchase/consumer/review",
  isPointOfNoReturn: false,
};

export const consumerFinancingChecklistStep: ConsumerFinancingStep = {
  id: 18,
  nameTranslation: "purchase:checklist_title",
  order: 6,
  href: "/purchase/consumer/checklist",
  isPointOfNoReturn: false,
};

export const allConsumerFinancingSteps = [
  consumerFinancingDetailsStep,
  consumerFinancingPersonalInfoStep,
  consumerFinancingCoveragesStep,
  consumerFinancingFinancingStep,
  consumerFinancingReviewStep,
  consumerFinancingChecklistStep,
];

export const getFirstStep = () => allConsumerFinancingSteps[0];

export const getNextStep = (currentStep: ConsumerFinancingStep) => {
  const steps = allConsumerFinancingSteps;
  const index = steps.findIndex((s) => s.id === currentStep.id);
  if (index === steps.length - 1 || index === -1) {
    return undefined;
  }
  return steps[index + 1];
};

export const getPreviousStep = (currentStep: ConsumerFinancingStep) => {
  const steps = allConsumerFinancingSteps;
  const index = steps.findIndex((s) => s.id === currentStep.id);

  if (index === 0 || index === -1) {
    return undefined;
  }

  return steps[index - 1];
};

export const getStepById = (id: number) =>
  allConsumerFinancingSteps.find((s) => s.id === id);

export const getStepPathname = (step: ConsumerFinancingStep, intro = false) =>
  intro ? step.introHref || step.href : step.href;

export const isStepAccessible = (
  currentStep: ConsumerFinancingStep,
  accessedStep: ConsumerFinancingStep,
  purchaseHasOverwriteCandidate = false
) => {
  if (currentStep.isPointOfNoReturn && !purchaseHasOverwriteCandidate) {
    return currentStep === accessedStep;
  }

  return accessedStep.order <= currentStep.order;
};

export const isValidConsumerFinancingSource = (source: string | undefined) =>
  source &&
  Sources.find((s) => s.name.toLowerCase() === source.toLowerCase()) !==
    undefined;
